import Konva from 'konva';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { RenderStyles } from './renderStyles';
import { useStageRefs } from './useStageRefsMap';

export type KonvaStageContext = {
  stageRef: React.RefObject<Konva.Stage | null>;
  containerId: string;
  stageScaleX: number;
  stageScaleY: number;
  stageWidth: number;
  stageHeight: number;
  setStageDimensions: ({
    stageWidth,
    stageHeight,
    stageScaleX,
    stageScaleY,
  }: {
    stageWidth: number;
    stageHeight: number;
    stageScaleX: number;
    stageScaleY: number;
  }) => void;
  renderStyles: RenderStyles;
  positionId?: string;
};

export const KonvaContext = createContext<KonvaStageContext | null>(null);

export const useKonva = () => {
  const context = useContext(KonvaContext);
  if (!context) {
    throw new Error('useKonva must be used within a KonvaProvider');
  }
  return context;
};

type KonvaProviderProps = {
  children: React.ReactNode;
  containerId: string;
  stageWidth?: number;
  stageHeight?: number;
  stageScaleX?: number;
  stageScaleY?: number;
  renderStyles: RenderStyles;
  positionId?: string;
};

export const KonvaProvider = ({
  children,
  containerId,
  stageWidth = 0,
  stageHeight = 0,
  stageScaleX = 1,
  stageScaleY = 1,
  renderStyles,
  positionId,
}: KonvaProviderProps) => {
  const [stageDimensions, setStageDimensions] = useState({
    stageScaleX,
    stageScaleY,
    stageWidth,
    stageHeight,
  });
  const { unregisterStageRef, registerStageRef } = useStageRefs();

  const stageRef = React.useRef<Konva.Stage>(null);

  // Notify parent component when stage ref is available
  useEffect(() => {
    if (positionId && stageRef.current) {
      registerStageRef(positionId, stageRef as React.RefObject<Konva.Stage>);
    }

    return () => {
      if (positionId) {
        unregisterStageRef(positionId);
      }
    };
  }, [positionId, registerStageRef, unregisterStageRef]);

  return (
    <KonvaContext.Provider
      value={{
        ...stageDimensions,
        containerId,
        setStageDimensions,
        stageRef,
        renderStyles,
        positionId,
      }}
    >
      {children}
    </KonvaContext.Provider>
  );
};
